import {
  forwardRef,
  useRef,
  type ComponentPropsWithRef,
  type ChangeEvent,
} from "react";
import { useMergeRefs } from "@floating-ui/react";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./CheckboxBase.styled";
import { useSyncIndeterminate } from "./CheckboxBase.utils";

export type CheckboxBaseProps = PropsWithOptionalTestID &
  Omit<ComponentPropsWithRef<"input">, "type" | "style" | "className"> & {
    hasError?: boolean;
    isIndeterminate?: boolean;
  };

const CheckboxBase = forwardRef<HTMLInputElement, CheckboxBaseProps>(
  (props, ref) => {
    const {
      hasError,
      isIndeterminate,
      testID = "checkbox",
      ...inputProps
    } = props;

    const localRef = useRef<HTMLInputElement>(null);
    useSyncIndeterminate(localRef, isIndeterminate);

    const mergedRefs = useMergeRefs([localRef, ref]);

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      event.target.indeterminate = Boolean(isIndeterminate);
      inputProps.onChange?.(event);
    }

    return (
      <S.CustomCheckbox $hasError={hasError}>
        <S.HiddenInput
          {...inputProps}
          ref={mergedRefs}
          type="checkbox"
          onChange={handleChange}
          data-testid={testID}
        />

        <S.IndeterminateIcon />
        <S.CheckedIcon />
      </S.CustomCheckbox>
    );
  }
);

CheckboxBase.displayName = "CheckboxBase";

export default CheckboxBase;
